import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { FilterAdapter, useUrlFilterController } from 'app/components/filters/url-filters';

export const MauMinAdapter: FilterAdapter<number> = {
  key: 'mauMin',
  fromUrl: (sp) => {
    const v = sp.get('mauMin');
    return v !== null ? Number(v) : null;
  },
  toUrl: (sp, value) => {
    const next = new URLSearchParams(sp.toString());
    if (value === null || value === undefined) {
      next.delete('mauMin');
    } else {
      next.set('mauMin', String(value));
    }
    return next;
  },
  toKey: (v) => `mauMin:${v ?? ''}`
};

export const MauMaxAdapter: FilterAdapter<number> = {
  key: 'mauMax',
  fromUrl: (sp) => {
    const v = sp.get('mauMax');
    return v !== null ? Number(v) : null;
  },
  toUrl: (sp, value) => {
    const next = new URLSearchParams(sp.toString());
    if (value === null || value === undefined) {
      next.delete('mauMax');
    } else {
      next.set('mauMax', String(value));
    }
    return next;
  },
  toKey: (v) => `mauMax:${v ?? ''}`
};

const StyledMauInput = styled.input(({ theme }) =>
  theme.withMedia({
    background: theme.styleguideColors.fillSecondary,
    border: 'none',
    borderRadius: '4px',
    fontSize: '14px',
    padding: '9px 12px',
    color: theme.styleguideColors.contentPrimary,
    outline: 'none',
    width: '110px',
    '::placeholder': {
      color: theme.styleguideColors.contentSecondary
    }
  })
);

const DEBOUNCE_MS = 600;

function useDebouncedNumber(
  committed: number | null,
  onCommit: (v: number | null) => void
): [string, (e: React.ChangeEvent<HTMLInputElement>) => void] {
  const [draft, setDraft] = useState(committed !== null ? String(committed) : '');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setDraft(committed !== null ? String(committed) : '');
  }, [committed]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setDraft(raw);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const parsed = raw === '' ? null : Number(raw);
      onCommit(parsed !== null && !isNaN(parsed) ? parsed : null);
    }, DEBOUNCE_MS);
  };

  return [draft, handleChange];
}

export const MauFilter = () => {
  const ctl = useUrlFilterController();
  const minCommitted = ctl.get(MauMinAdapter);
  const maxCommitted = ctl.get(MauMaxAdapter);

  const [minDraft, handleMinChange] = useDebouncedNumber(minCommitted, (v) =>
    ctl.set(MauMinAdapter, v)
  );
  const [maxDraft, handleMaxChange] = useDebouncedNumber(maxCommitted, (v) =>
    ctl.set(MauMaxAdapter, v)
  );

  return (
    <>
      <StyledMauInput
        type="number"
        value={minDraft}
        onChange={handleMinChange}
        placeholder="MAU min"
        min={0}
      />
      <StyledMauInput
        type="number"
        value={maxDraft}
        onChange={handleMaxChange}
        placeholder="MAU max"
        min={0}
      />
    </>
  );
};
